import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import React, { useMemo } from 'react';
import { Download, ExternalLink, Package } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import ReactMarkdown from 'react-markdown';
import type {
    ArticleGraph,
    ComparableFieldDefinition,
    ComparisonSettings,
    ProductComparerAiAnalysisResponse,
} from '../../types/productComparer';

interface ProductComparerTableProps {
    items: ArticleGraph[];
    settings: ComparisonSettings;
    aiAnalysis: ProductComparerAiAnalysisResponse | null;
    availableFields: ComparableFieldDefinition[];
    onExportExcel: () => void;
    exportLoading: boolean;
}

function getEntityFieldValue(
    entity: Record<string, unknown>,
    additionalFields: Record<string, string> | undefined,
    propertyName: string,
    sourceFieldTypeId?: string,
    sourcePrefix?: string,
): string {
    const directValue = entity[lowerFirst(propertyName)];
    if (directValue !== undefined) {
        return formatValue(directValue);
    }

    const additionalValue = getAdditionalFieldValue(additionalFields, sourceFieldTypeId, propertyName);
    if (additionalValue != null) {
        return formatValue(additionalValue);
    }

    if (sourceFieldTypeId) {
        const altKey = sourcePrefix && sourceFieldTypeId.startsWith(sourcePrefix)
            ? sourceFieldTypeId.slice(sourcePrefix.length)
            : sourceFieldTypeId;
        const altValue = getAdditionalFieldValue(additionalFields, altKey);
        if (altValue != null) {
            return formatValue(altValue);
        }
    }

    return '—';
}

function joinDistinctComparableValues(values: string[]): string {
    const distinctValues = Array.from(new Set(values.filter(value => value !== '—')));
    return distinctValues.length > 0 ? distinctValues.join(', ') : '—';
}

function getInRiverEntityUrl(entityId: number): string {
    return `https://portal2-prod1a-euw.productmarketingcloud.com/app/enrich#entity/${entityId}/details`;
}

interface RowData {
    fieldId: string;
    sourceFieldTypeId?: string;
    label: string;
    entityType: string;
    category: string;
    values: string[];
}

interface RowGroup {
    key: string;
    entityType: string;
    category: string;
    rows: RowData[];
}

function getAdditionalFieldValue(additionalFields: Record<string, string> | undefined, ...candidates: Array<string | undefined>): string | undefined {
    if (!additionalFields) {
        return undefined;
    }

    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }

        const exactValue = additionalFields[candidate];
        if (exactValue != null && exactValue.trim().length > 0) {
            return exactValue;
        }

        const match = Object.entries(additionalFields)
            .find(([key]) => key.localeCompare(candidate, undefined, { sensitivity: 'accent' }) === 0);
        if (match && match[1].trim().length > 0) {
            return match[1];
        }
    }

    return undefined;
}

/**
 * Resolves a dotted fieldId to a display value from the article graph.
 * Falls back to the first linked entity when the field references Nutrient or Package.
 */
function resolveField(article: ArticleGraph, fieldId: string, sourceFieldTypeId?: string): string {
    // ── Item-level fields ──
    switch (fieldId) {
        case 'ItemNumber':       return article.itemNumber ?? '—';
        case 'InternalName':     return article.internalName ?? '—';
        case 'Gtin':             return article.gtin ?? '—';
        case 'ItemBrand':        return article.itemBrand ?? '—';
        case 'ItemProductCategory': return article.itemProductCategory ?? '—';
        case 'ItemProductGroup': return article.itemProductGroup ?? '—';
        case 'ItemCommercialName': return article.itemCommercialName ?? '—';
        case 'NetWeightGrams':   return article.netWeightGrams != null ? `${article.netWeightGrams}` : '—';
    }

    // ── Nutrient-level fields ──
    if (fieldId.startsWith('Nutrient.')) {
        const prop = fieldId.split('.')[1];
        const nutrients = article.nutrients ?? [];
        if (nutrients.length === 0) return '—';

        const values = nutrients.map(nutrient => getEntityFieldValue(
            nutrient as unknown as Record<string, unknown>,
            nutrient.additionalFields,
            prop,
            sourceFieldTypeId,
            'Nutrient'));

        return joinDistinctComparableValues(values);
    }

    // ── Package-level fields ──
    if (fieldId.startsWith('Package.')) {
        const prop = fieldId.split('.')[1];
        const packages = article.packages ?? [];
        if (packages.length === 0) return '—';

        const values = packages.map(pkg => getEntityFieldValue(
            pkg as unknown as Record<string, unknown>,
            pkg.additionalFields,
            prop,
            sourceFieldTypeId,
            'Package'));

        return joinDistinctComparableValues(values);
    }

    // ── Generic Item-level fallback (e.g. LocaleString fields like ItemDescriptionSales) ──
    const itemRecord = article as unknown as Record<string, unknown>;
    const directValue = itemRecord[fieldId];
    if (directValue !== undefined) {
        return formatValue(directValue);
    }

    const camelCaseValue = itemRecord[lowerFirst(fieldId)];
    if (camelCaseValue !== undefined) {
        return formatValue(camelCaseValue);
    }

    const itemAdditionalValue = getAdditionalFieldValue(
        article.additionalFields,
        sourceFieldTypeId,
        fieldId,
        lowerFirst(fieldId),
        sourceFieldTypeId?.startsWith('Item') ? sourceFieldTypeId.slice('Item'.length) : sourceFieldTypeId,
    );
    if (itemAdditionalValue != null) {
        return formatValue(itemAdditionalValue);
    }

    return '—';
}

function lowerFirst(s: string): string {
    return s.charAt(0).toLowerCase() + s.slice(1);
}

const preferredLocale = 'en';

function isLocaleKey(key: string): boolean {
    return /^[a-z]{2}(-[a-z]{2})?$/i.test(key);
}

function getLocaleStringValue(obj: Record<string, unknown>): string | null {
    const localeEntries = Object.entries(obj)
        .filter(([key]) => isLocaleKey(key));

    if (localeEntries.length === 0) {
        return null;
    }

    const preferred = localeEntries.find(([locale]) => locale.toLowerCase() === preferredLocale)
        ?? localeEntries.find(([locale]) => locale.toLowerCase().startsWith(`${preferredLocale}-`));

    const selectedLocaleValue = preferred?.[1] ?? localeEntries[0][1];
    const normalized = normalizeComparableValue(selectedLocaleValue);
    return normalized ?? null;
}

function normalizeComparableValue(value: unknown): string | null {
    if (value == null) return null;

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number' || typeof value === 'bigint') return String(value);

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            return null;
        }

        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                const parsed = JSON.parse(trimmed) as unknown;
                const parsedValue = normalizeComparableValue(parsed);
                if (parsedValue) {
                    return parsedValue;
                }
            } catch { }
        }

        return trimmed;
    }

    if (Array.isArray(value)) {
        const values = value
            .map(normalizeComparableValue)
            .filter((item): item is string => Boolean(item));

        return values.length > 0
            ? Array.from(new Set(values)).join(', ')
            : null;
    }

    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;

        const localeValue = getLocaleStringValue(obj);
        if (localeValue) {
            return localeValue;
        }

        for (const propertyName of ['value', 'label', 'displayName', 'name', 'text']) {
            if (propertyName in obj) {
                const nestedValue = normalizeComparableValue(obj[propertyName]);
                if (nestedValue) {
                    return nestedValue;
                }
            }
        }

        const values = Object.values(obj)
            .map(normalizeComparableValue)
            .filter((item): item is string => Boolean(item));

        return values.length > 0
            ? Array.from(new Set(values)).join(', ')
            : null;
    }

    const fallback = String(value).trim();
    return fallback.length > 0 ? fallback : null;
}

function formatValue(val: unknown): string {
    return normalizeComparableValue(val) ?? '—';
}

/** Human-friendly label for a field ID. */
function fieldLabel(fieldId: string): string {
    const labels: Record<string, string> = {
        'ItemNumber': 'Item Number',
        'InternalName': 'Name',
        'Gtin': 'GTIN',
        'ItemBrand': 'Brand',
        'ItemProductCategory': 'Category',
        'ItemProductGroup': 'Product Group',
        'ItemCommercialName': 'Commercial Name',
        'NetWeightGrams': 'Net Weight (g)',
    };
    if (labels[fieldId]) return labels[fieldId];

    // For dotted IDs, use the part after the dot with spacing
    const parts = fieldId.split('.');
    const raw = parts[parts.length - 1];
    return raw.replace(/([A-Z])/g, ' $1').trim();
}

export function ProductComparerTable({ items, settings, aiAnalysis, availableFields, onExportExcel, exportLoading }: ProductComparerTableProps) {
    const rows: RowData[] = useMemo(() => {
        const metadataByFieldId = new Map<string, ComparableFieldDefinition>();
        for (const field of availableFields) {
            metadataByFieldId.set(field.fieldId.toLowerCase(), field);
        }

        return settings.selectedFieldIds.map(fieldId => {
            const metadata = metadataByFieldId.get(fieldId.toLowerCase());
            const entityType = metadata?.entityType ?? (fieldId.includes('.') ? fieldId.split('.')[0] : 'Item');
            const category = metadata?.category ?? 'General';

            return {
                fieldId,
                sourceFieldTypeId: metadata?.sourceFieldTypeId,
                label: metadata?.displayName ?? fieldLabel(fieldId),
                entityType,
                category,
                values: items.map(item => resolveField(item, fieldId, metadata?.sourceFieldTypeId)),
            };
        });
    }, [availableFields, items, settings]);

    const groupedRows: RowGroup[] = useMemo(() => {
        const entityOrder: Record<string, number> = {
            Item: 0,
            Nutrient: 1,
            Package: 2,
        };

        const groups = new Map<string, RowGroup>();

        for (const row of rows) {
            const key = `${row.entityType}__${row.category}`;
            const existing = groups.get(key);
            if (existing) {
                existing.rows.push(row);
            } else {
                groups.set(key, {
                    key,
                    entityType: row.entityType,
                    category: row.category,
                    rows: [row],
                });
            }
        }

        return Array.from(groups.values())
            .sort((a, b) => {
                const entityCompare = (entityOrder[a.entityType] ?? 99) - (entityOrder[b.entityType] ?? 99);
                if (entityCompare !== 0) return entityCompare;
                return a.category.localeCompare(b.category);
            });
    }, [rows]);

    if (items.length === 0) return null;

    // Determine if values differ per row (for highlighting)
    const hasDifference = (values: string[]) => {
        const unique = new Set(values);
        return unique.size > 1;
    };

    const buildPreview = (content: string | undefined) => {
        if (!content) return 'No analysis available.';

        const plainText = content
            .replace(/```[\s\S]*?```/g, ' ')
            .replace(/`([^`]+)`/g, '$1')
            .replace(/\*\*([^*]+)\*\*/g, '$1')
            .replace(/\*([^*]+)\*/g, '$1')
            .replace(/^#{1,6}\s+/gm, '')
            .replace(/\[(.*?)\]\(.*?\)/g, '$1')
            .replace(/\s+/g, ' ')
            .trim();

        return plainText.length > 220
            ? `${plainText.slice(0, 220).trim()}...`
            : plainText;
    };

    const innovationPreview = buildPreview(aiAnalysis?.innovationInsight);
    const commercialPreview = buildPreview(aiAnalysis?.commercialInsight);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <div>
                    <h2 className="text-xl font-bold">Comparison Report</h2>
                    <p className="text-sm text-muted-foreground">
                        Comparing {items.length} items across {settings.selectedFieldIds.length} fields
                    </p>
                </div>
                <div className="ml-auto">
                    <CapabilityButton capability={P.ProductComparisonExport} onClick={onExportExcel} disabled={exportLoading}>
                        <Download className="mr-2 h-4 w-4" />
                        {exportLoading ? 'Exporting...' : 'Download Excel'}
                    </CapabilityButton>
                </div>
            </div>

            <div className="space-y-4">
                <Accordion type="multiple" className="rounded-lg border px-4">
                    <AccordionItem value="innovation" className="border-b">
                        <AccordionTrigger className="hover:no-underline">
                            <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
                                <h3 className="text-sm font-semibold">Innovation Perspective</h3>
                                <p className="text-xs text-muted-foreground">
                                    {settings.innovationStrategy
                                        ? 'Analyzed against your innovation strategy.'
                                        : 'No innovation strategy provided. Objective bakery goods analysis applied.'}
                                </p>
                                <p className="line-clamp-2 text-xs text-muted-foreground">
                                    {innovationPreview}
                                </p>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="rounded-md bg-muted/30 p-3">
                                <article className="prose prose-sm max-w-none text-sm leading-relaxed">
                                    <ReactMarkdown>
                                        {aiAnalysis?.innovationInsight ?? 'No innovation analysis available.'}
                                    </ReactMarkdown>
                                </article>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="commercial" className="border-b-0">
                        <AccordionTrigger className="hover:no-underline">
                            <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
                                <h3 className="text-sm font-semibold">Commercial Perspective</h3>
                                <p className="text-xs text-muted-foreground">
                                    {settings.salesStrategy
                                        ? 'Analyzed against your sales strategy.'
                                        : 'No sales strategy provided. Objective bakery goods analysis applied.'}
                                </p>
                                <p className="line-clamp-2 text-xs text-muted-foreground">
                                    {commercialPreview}
                                </p>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="rounded-md bg-muted/30 p-3">
                                <article className="prose prose-sm max-w-none text-sm leading-relaxed">
                                    <ReactMarkdown>
                                        {aiAnalysis?.commercialInsight ?? 'No commercial analysis available.'}
                                    </ReactMarkdown>
                                </article>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border">
                <table className="w-full min-w-[600px] text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                Field
                            </th>
                            {items.map(item => (
                                <th key={item.entityId} className="p-3 text-left">
                                    <div className="flex items-start gap-2">
                                        {item.primaryImageUrl ? (
                                            <img
                                                src={item.primaryImageUrl}
                                                alt={item.internalName ?? ''}
                                                className="h-10 w-10 rounded object-contain border"
                                            />
                                        ) : (
                                            <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                                                <Package className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-semibold leading-tight">
                                                {item.internalName ?? `Item ${item.entityId}`}
                                            </p>
                                            <p className="text-xs text-muted-foreground font-mono">
                                                {item.itemNumber ?? item.entityId}
                                            </p>
                                            <a
                                                href={getInRiverEntityUrl(item.entityId)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                Open in InRiver
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {groupedRows.map(group => (
                            <React.Fragment key={group.key}>
                                <tr className="bg-muted/40">
                                    <td className="p-3 font-semibold" colSpan={items.length + 1}>
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                            <Badge variant="outline" className="text-[10px]">
                                                {group.entityType}
                                            </Badge>
                                            <span>{group.category}</span>
                                        </div>
                                    </td>
                                </tr>
                                {group.rows.map(row => {
                                    const diff = hasDifference(row.values);
                                    return (
                                        <tr
                                            key={row.fieldId}
                                            className={`transition-colors hover:bg-accent/50 ${diff ? '' : 'opacity-70'}`}
                                        >
                                            <td className="p-3 font-medium">{row.label}</td>
                                            {row.values.map((val, vIdx) => (
                                                <td
                                                    key={`${row.fieldId}-${vIdx}`}
                                                    className={`p-3 ${diff ? 'font-medium' : 'text-muted-foreground'}`}
                                                >
                                                    {val}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Nutrient Summary */}
            {items.some(i => i.nutrients && i.nutrients.length > 0) && (
                <div className="rounded-lg border p-4">
                    <h3 className="mb-2 text-sm font-semibold">Nutrients Overview</h3>
                    <p className="text-xs text-muted-foreground">
                        {items.map(i => `${i.internalName ?? i.entityId}: ${i.nutrients?.length ?? 0} nutrient(s)`).join(' · ')}
                    </p>
                </div>
            )}

            {/* Package Summary */}
            {items.some(i => i.packages && i.packages.length > 0) && (
                <div className="rounded-lg border p-4">
                    <h3 className="mb-2 text-sm font-semibold">Packages Overview</h3>
                    <p className="text-xs text-muted-foreground">
                        {items.map(i => `${i.internalName ?? i.entityId}: ${i.packages?.length ?? 0} package(s)`).join(' · ')}
                    </p>
                </div>
            )}

            <div className="text-center text-xs text-muted-foreground">
                Data sourced from InRiver PIM via Chiwen.Services.InRiver.API
            </div>
        </div>
    );
}
